import { Button } from '@jouzen/ecom-components/v3/Button';
import { CardButton } from '@jouzen/ecom-components/v3/CardButton';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import ArtDirectionImage from '@/app/components/Image/ArtDirectionImage';

import { useKeepInView } from './hooks/useKeepInView';

export interface LandingPageProfile {
  readonly description: MessageKey;
  readonly image: string;
  readonly imageAlt: MessageKey;
  readonly imageMobile: string;
  readonly name: string;
  readonly onLinkClick?: () => void | undefined;
}

type LandingPageProfileCardProps = LandingPageProfile & {
  readonly expanded: boolean;
  readonly onButtonClick: () => void;
};

const LandingPageProfileCard = ({
  description,
  expanded,
  image,
  imageAlt,
  imageMobile,
  name,
  onButtonClick,
  onLinkClick,
}: LandingPageProfileCardProps) => {
  const divRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const t = useTranslations();

  useKeepInView(divRef, buttonRef, expanded);

  return (
    <div
      className="relative w-fit shrink-0 overflow-hidden rounded-xl bg-neutral-700 sm:h-[520px] sm:snap-center lg:h-[370px] lg:snap-none"
      data-cy="landing-page-profile-card"
      ref={divRef}
    >
      <div
        className={cx(
          'absolute top-4 right-4 z-10 hidden transition-transform lg:block',
          {
            'rotate-45': expanded,
          },
        )}
      >
        <CardButton
          color="light"
          data-cy="landing-page-profile-card-expand-button"
          variant={'expand'}
          size="xlarge"
          onClick={onButtonClick}
        />
      </div>
      <div className="flex h-full flex-col lg:flex-row">
        <div className="relative w-[277px] shrink-0 overflow-hidden sm:aspect-square sm:h-[277px] lg:aspect-auto lg:h-full">
          <ArtDirectionImage
            className="object-cover"
            localizedAlt={imageAlt}
            srcSet={[
              { src: image, media: `(min-width: ${BREAKPOINTS.large})` },
              {
                src: imageMobile,
                media: `(max-width: ${BREAKPOINTS.large})`,
              },
            ]}
            fill
          />
        </div>

        {/* DESKTOP CONTENT */}
        <div
          className={cx(
            'h-full w-0 overflow-hidden transition-all sm:hidden lg:block',
            {
              'w-72': expanded,
            },
          )}
        >
          <div className="flex h-full w-72 flex-col justify-end gap-4 overflow-hidden bg-neutral-700 p-6 text-sandstone-200">
            <Typography color="inherit" className="text-2xl font-bold">
              {name}
            </Typography>
            <Typography color="inherit" variant={'base'}>
              {t(description)}
            </Typography>
            {onLinkClick && (
              <Button
                aria-label={t('view_full_bio')}
                className="text-left text-sandstone-200 hover:opacity-70"
                data-cy="landing-page-profile-card-desktop-button"
                onClick={onLinkClick}
                tabIndex={expanded ? undefined : -1}
                variant="primary-text-link"
              >
                <span ref={buttonRef} className="underline">
                  {t('view_full_bio')}
                </span>
              </Button>
            )}
          </div>
        </div>

        {/* MOBILE CONTENT */}
        <div
          className={cx(
            'flex h-full w-[277px] grow flex-col justify-between overflow-hidden p-6 transition-all lg:hidden',
            {
              'bg-neutral-500': expanded,
              'bg-neutral-700': !expanded,
            },
          )}
        >
          <div className="flex flex-col justify-between gap-2 text-sandstone-200">
            <Typography color="inherit" className="text-lg font-bold">
              {name}
            </Typography>
            <Typography color="inherit" variant={'body-small'}>
              {t(description)}
            </Typography>
          </div>
          {onLinkClick && (
            <Button
              aria-label={t('view_full_bio')}
              className="text-left text-sandstone-200 hover:opacity-70"
              data-cy="landing-page-profile-card-mobile-button"
              onClick={onLinkClick}
              variant="primary-text-link"
            >
              <span className="underline">{t('view_full_bio')}</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingPageProfileCard;
